import { type CollisionContact } from './collision.ts';
import { BOW_OFFSET_PX, CRASH_HARSHNESS, MOVEMENT_RADIUS, TURN_TAX } from './constants.ts';
import { type CrashImpact } from './crash.ts';
import { DEFAULT_WIND_FEEL, windOffsetPx, type WindFeel } from './environment/wind.ts';
import { reportsFromContacts, withCrossingAngle, type ContactReport } from './damage.ts';
import type { HullDimensions } from './hullSize.ts';
import type { MapDocument } from './map/types.ts';
import { isValidDestination, projectMovementArea, resolveMove } from './movement.ts';
import { separateFleet, type BoatHit, type FleetRun } from './modules/host.ts';
import { projectMovePath, type MovePath } from './path.ts';
import { advanceThroughTerrain } from './terrainRun.ts';
import type { ReachAxes } from './boat/reach.ts';
import type { BoatState, TurnPhase, Vec2 } from './types.ts';

export type CommitResult = {
  boat: BoatState;
  phase: 'resolving';
  collisions: CollisionContact[];
  reports: ContactReport[];
  /** Sim path the hull should travel — inbound clip plus the rebound on a solid hit. */
  path: MovePath | null;
  /** Aimed arc up to the first solid, so chrome can point at the wall it would meet. */
  impactPath: MovePath | null;
  clipped: boolean;
  /** First-hit impact so the play log can show shore angle; slider re-plans instead. */
  crash: CrashImpact | null;
  /**
   * Boats this move ran into. Empty unless the caller passed the rest of the
   * fleet. The damage pass reads the impulse on each; nothing spends it yet.
   */
  boatContacts: BoatHit[];
};

/**
 * The rest of the fleet, for a move that has to share the water.
 *
 * Hover can only know where the other boats are *now*, never where their own
 * clicks will take them, so they enter as parked hulls. In turn-by-turn play
 * that is also the truth; in a simultaneous round it is what the player can
 * honestly see.
 */
export type FleetContext = {
  /** The moving boat's own hull. */
  hull: HullDimensions;
  others: readonly { id: number; boat: BoatState; dimensions: HullDimensions }[];
  /** Seat id of the boat being moved, so a contact names the right pair. */
  id?: number;
};

/** A planned move together with what it did to the rest of the fleet. */
export type FleetMove = {
  commit: CommitResult;
  /** Every seat's landed run. Empty when the move had the sea to itself. */
  runs: readonly FleetRun[];
};

/**
 * Planning-time commit without the phase gate. Hover uses this so a shore-bound
 * click can preview the clipped crash path.
 */
export function planMove(
  boat: BoatState,
  point: Vec2,
  radius: number = MOVEMENT_RADIUS,
  map: MapDocument,
  crashHarshness: number = CRASH_HARSHNESS,
  turnTax: number = TURN_TAX,
  windFeel: WindFeel = DEFAULT_WIND_FEEL,
  /**
   * Heading-aligned oval. Omitted, the disk of `radius` is the reach.
   * Equal axes are that disk; callers keep the circle path in that case.
   */
  reach: ReachAxes | null = null,
  bowOffsetPx: number = BOW_OFFSET_PX,
  /** Coast length already resolved by the caller. Omitted, the centre uses `boat.speed`. */
  coastPx?: number,
  /** Kept speed already resolved by the caller. Omitted, the chord (and turn tax) stands. */
  keptSpeed?: number,
  /** The other boats on the water. Omitted, this move has the sea to itself. */
  fleet?: FleetContext,
): CommitResult | null {
  return planFleetMove(
    boat,
    point,
    radius,
    map,
    crashHarshness,
    turnTax,
    windFeel,
    reach,
    bowOffsetPx,
    coastPx,
    keptSpeed,
    fleet,
  )?.commit ?? null;
}

/**
 * The same plan, with what it did to everyone else. Hover only wants its own
 * seat back (`planMove`); the match has to write every boat the move touched.
 */
export function planFleetMove(
  boat: BoatState,
  point: Vec2,
  radius: number = MOVEMENT_RADIUS,
  map: MapDocument,
  crashHarshness: number = CRASH_HARSHNESS,
  turnTax: number = TURN_TAX,
  windFeel: WindFeel = DEFAULT_WIND_FEEL,
  reach: ReachAxes | null = null,
  bowOffsetPx: number = BOW_OFFSET_PX,
  coastPx?: number,
  keptSpeed?: number,
  fleet?: FleetContext,
): FleetMove | null {
  // Wind shifts the reachable set (the disk centre), so the same feel gates the hover
  // preview, the live commit, and a replay reconstruction. The click still lands on `point`.
  const windOffset = windOffsetPx(map.environment, boat.speed, windFeel);

  if (
    !isValidDestination(
      boat,
      point,
      projectMovementArea(boat, radius, windOffset, coastPx ?? boat.speed),
      reach,
      bowOffsetPx,
    )
  ) {
    return null;
  }

  const aimed = projectMovePath(boat, point, 'arc', turnTax);
  const landedIfClear = resolveMove(boat, point, turnTax, keptSpeed);

  if (fleet && fleet.others.length > 0) {
    return throughFleet(map, boat, aimed, landedIfClear, fleet, crashHarshness, turnTax);
  }

  const run = advanceThroughTerrain(
    map,
    boat,
    aimed,
    landedIfClear,
    crashHarshness,
    turnTax,
  );

  if (!run) {
    return null;
  }

  const reports = reportsFromContacts(map, run.contacts, boat);

  return {
    commit: {
      boat: run.boat,
      phase: 'resolving',
      collisions: run.contacts,
      reports: run.clipped ? withCrossingAngle(reports, run.crossingDeg) : reports,
      path: run.clipped ? run.path : aimed,
      impactPath: run.impactPath,
      clipped: run.clipped,
      crash: run.crash,
      boatContacts: [],
    },
    runs: [],
  };
}

/**
 * The same move, resolved against the rest of the fleet. This is the one caller
 * of the fleet sweep that only wants its own seat back; `resolveFleetTurn`
 * itself is what the match uses when it has to write every boat.
 */
function throughFleet(
  map: MapDocument,
  boat: BoatState,
  aimed: MovePath | null,
  landedIfClear: BoatState,
  fleet: FleetContext,
  crashHarshness: number,
  turnTax: number,
): FleetMove | null {
  const id = fleet.id ?? -1;
  const result = separateFleet(
    map,
    [
      { id, boat, dimensions: fleet.hull, intended: aimed, landedIfClear },
      ...fleet.others.map((other) => ({
        id: other.id,
        boat: other.boat,
        dimensions: other.dimensions,
        intended: null,
      })),
    ],
    crashHarshness,
    turnTax,
  );
  const mine = result.runs.find((run) => run.id === id);

  if (!mine || (!mine.path && aimed)) {
    return null;
  }

  const reports = reportsFromContacts(map, mine.contacts, boat);

  return {
    commit: {
      boat: mine.boat,
      phase: 'resolving',
      collisions: mine.contacts,
      reports: mine.clipped ? withCrossingAngle(reports, mine.crossingDeg) : reports,
      path: mine.path,
      impactPath: mine.impactPath,
      clipped: mine.clipped || mine.shoved,
      crash: mine.crash,
      boatContacts: result.hits.filter(
        (contact) => contact.fromId === id || contact.intoId === id,
      ),
    },
    runs: result.runs,
  };
}

export function tryCommit(
  phase: TurnPhase,
  boat: BoatState,
  point: Vec2,
  radius: number = MOVEMENT_RADIUS,
  map: MapDocument,
  crashHarshness: number = CRASH_HARSHNESS,
  turnTax: number = TURN_TAX,
  windFeel: WindFeel = DEFAULT_WIND_FEEL,
  reach: ReachAxes | null = null,
  bowOffsetPx: number = BOW_OFFSET_PX,
  coastPx?: number,
  keptSpeed?: number,
  fleet?: FleetContext,
): CommitResult | null {
  return tryCommitFleet(
    phase,
    boat,
    point,
    radius,
    map,
    crashHarshness,
    turnTax,
    windFeel,
    reach,
    bowOffsetPx,
    coastPx,
    keptSpeed,
    fleet,
  )?.commit ?? null;
}

/** `tryCommit` for a caller that also has to write the boats this move pushed. */
export function tryCommitFleet(
  phase: TurnPhase,
  boat: BoatState,
  point: Vec2,
  radius: number = MOVEMENT_RADIUS,
  map: MapDocument,
  crashHarshness: number = CRASH_HARSHNESS,
  turnTax: number = TURN_TAX,
  windFeel: WindFeel = DEFAULT_WIND_FEEL,
  reach: ReachAxes | null = null,
  bowOffsetPx: number = BOW_OFFSET_PX,
  coastPx?: number,
  keptSpeed?: number,
  fleet?: FleetContext,
): FleetMove | null {
  if (phase !== 'planning') {
    return null;
  }

  return planFleetMove(
    boat,
    point,
    radius,
    map,
    crashHarshness,
    turnTax,
    windFeel,
    reach,
    bowOffsetPx,
    coastPx,
    keptSpeed,
    fleet,
  );
}

export function completeResolve(nextBoat: BoatState): {
  boat: BoatState;
  phase: 'planning';
} {
  return {
    boat: nextBoat,
    phase: 'planning',
  };
}
